/* select-panel.js - the built drop down, shared by the country and currency row
   (topbar and footer, so twice on every route) and by the collection and search
   sort control. Emitted once per page as a deferred script by
   sections/footer.liquid; it scans the whole document, so one tag serves every
   instance on the route, and a route rendered without a footer simply keeps the
   native selects, which is the floor this file is built around.

   WHAT IT REPLACES AND WHAT IT LEAVES ALONE. An open native <select> paints its
   options over the page out of browser chrome that CSS cannot reach, and on iOS
   that chrome is a system wheel a few millimetres tall. So the visible half is
   rebuilt here -- a real <button> plus a real list in the top layer -- while the
   <select> itself stays in the document and stays the form's control: it keeps
   its name, it keeps its value, and `display: none` does not take a control out
   of a form submission (the HTML entry list skips a control for `disabled`,
   never for display). That is what keeps the localization form posting
   country_code on a page view where this file never arrived. This shop is live;
   the country row changes the prices a shopper is quoted; the safe path is the
   one that needs no script.

   THE OPTION LIST IS READ OFF THE <select> AND NEVER DUPLICATED IN MARKUP. One
   source of truth for twenty eight countries and nine sort orders, no second
   list in Liquid to drift out of step, and nothing added to the page's bytes.

   EVERY ID IS DERIVED FROM THE SELECT'S OWN ID, which the server has already
   made unique per host (sections/topbar.liquid mints TopbarCountry-<section.id>,
   sections/footer.liquid FooterCountry-<section.id>). Nothing here counts
   instances: a counter cannot be trusted to cross a render boundary in this
   theme, and a component that appears twice on every page is exactly where that
   failure shows up as two controls answering to one id. */
(function () {
  'use strict';

  var PP = window.PP;
  if (!PP) return;

  var PHONE = '(max-width: 767px)';

  /* Ends a close that transitionend never reported, which is what a backgrounded
     tab looks like. Must stay comfortably above the sheet's own --pp-dur-drawer
     (600ms) or it stops being a safety net and cuts the panel off mid slide.
     Same number and same reasoning as assets/menu-drawer.js. */
  var CLOSE_FALLBACK = 900;

  /* Desktop geometry. The panel is at least as wide as its trigger, never
     narrower than a country name reads at, never wider than the viewport can
     hold, and never closer than GUTTER to an edge. */
  var MIN_WIDTH = 260;
  var MAX_WIDTH = 400;
  var GUTTER = 8;
  var OFFSET = 6;

  var openInstance = null;

  function phone() {
    return window.matchMedia(PHONE).matches;
  }

  function el(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function arm(root) {
    if (root.hasAttribute('data-pp-select-armed')) return;

    var select = root.querySelector('[data-pp-select-native]');
    if (!select || !select.id || !select.options.length) return;

    /* No showModal, no swap. Every behaviour below leans on the top layer and on
       the platform's modal focus trap; without them the native select is a
       better control than anything this file could build. */
    if (typeof HTMLDialogElement !== 'function' || !HTMLDialogElement.prototype.showModal) return;

    root.setAttribute('data-pp-select-armed', '');

    var base = select.id;
    var label = document.querySelector('label[for="' + base + '"]');
    var titleText = (label && label.textContent.trim())
      || root.getAttribute('data-pp-select-label')
      || '';
    var closeText = root.getAttribute('data-pp-select-close') || '';

    if (label && !label.id) label.id = base + 'Label';

    /* A <label for> pointing at a control that is now display:none would be a
       label naming nothing clickable, and the toolbar's "Sort by" carries
       cursor: pointer. The name survives the removal: the trigger takes it by
       aria-labelledby, which does not need `for`, and the click is re-offered
       below. */
    if (label) label.removeAttribute('for');

    /* A FACETS REPAINT REPLACES THE TOOLBAR BY innerHTML, so a second arm of the
       same host is normal and the panel of the first one is still on <body>
       under the very id this one is about to mint. Removed by owner rather than
       left to be found by a query later: two nodes sharing an id is precisely
       the defect this file is written not to commit. */
    var stale = document.querySelectorAll('[data-pp-select-owner="' + base + '"]');
    for (var s = 0; s < stale.length; s += 1) stale[s].remove();

    var trigger = el('button', 'pp-select__trigger');
    trigger.type = 'button';
    trigger.id = base + 'Trigger';
    trigger.setAttribute('data-pp-select-trigger', '');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', base + 'Panel');

    var value = el('span', 'pp-select__value');
    value.id = base + 'Value';
    trigger.appendChild(value);

    /* The caret is MOVED, not cloned. The host already rendered one out of
       snippets/icon.liquid, it belonged to the select that is about to be
       hidden, and moving it keeps the icon registry the only source of that
       glyph without asking this file to carry SVG. */
    var caret = root.querySelector('[data-pp-select-caret]');
    if (caret) trigger.appendChild(caret);

    if (label && label.id) trigger.setAttribute('aria-labelledby', label.id + ' ' + value.id);

    /* Whatever the select was described by describes the trigger now: the sort
       control points at the toolbar's live region message. Copied generically
       rather than named here, so a host can add one without touching this file. */
    var describedBy = select.getAttribute('aria-describedby');
    if (describedBy) trigger.setAttribute('aria-describedby', describedBy);

    var dialog = el('dialog', 'pp-select__panel color-scheme-1');
    dialog.id = base + 'Panel';
    dialog.setAttribute('data-pp-select-panel', '');
    dialog.setAttribute('data-pp-select-owner', base);

    var sheet = el('div', 'pp-select__sheet');
    var head = el('div', 'pp-select__head');
    var title = el('p', 'pp-select__title text-subheading');
    title.id = base + 'Title';
    title.textContent = titleText;
    head.appendChild(title);

    if (closeText) {
      var closeButton = el('button', 'pp-select__close');
      closeButton.type = 'button';
      closeButton.textContent = closeText;
      closeButton.addEventListener('click', function () {
        closePanel();
      });
      head.appendChild(closeButton);
    }

    var list = el('div', 'pp-select__list');
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-labelledby', title.id);

    /* option.text, not textContent: the platform already strips and collapses
       the whitespace Liquid leaves around an option's label. */
    for (var i = 0; i < select.options.length; i += 1) {
      var option = select.options[i];
      var row = el('button', 'pp-select__option');
      row.type = 'button';
      row.id = base + 'Opt' + i;
      row.setAttribute('role', 'option');
      row.setAttribute('data-pp-select-option', '');
      row.setAttribute('data-pp-select-value', option.value);
      var text = el('span', 'pp-select__option-text');
      text.textContent = option.text;
      var tick = el('span', 'pp-select__tick');
      tick.setAttribute('aria-hidden', 'true');
      row.appendChild(text);
      row.appendChild(tick);
      list.appendChild(row);
    }

    sheet.appendChild(head);
    sheet.appendChild(list);
    dialog.appendChild(sheet);

    select.parentNode.insertBefore(trigger, select);

    /* APPENDED TO <body>, NOT NEXT TO THE TRIGGER. A top layer box is painted
       above every stacking context, but its containing block and the transforms
       it inherits are still its ancestors' business, and both hosts of this
       control sit inside rows that animate. <body> settles the question instead
       of relying on how one engine resolves it. */
    document.body.appendChild(dialog);

    function options() {
      return [].slice.call(list.querySelectorAll('[data-pp-select-option]'));
    }

    /* Read off the <select>, every time, rather than remembered here: facets.js
       reconciles the select after a filter change, and the shopper's own back
       button restores it. */
    function sync() {
      var current = select.value;
      var rows = options();
      var chosen = -1;
      for (var n = 0; n < rows.length; n += 1) {
        var isCurrent = rows[n].getAttribute('data-pp-select-value') === current;
        rows[n].setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        if (isCurrent) chosen = n;
      }
      for (var m = 0; m < rows.length; m += 1) rows[m].tabIndex = m === (chosen < 0 ? 0 : chosen) ? 0 : -1;
      var picked = select.options[select.selectedIndex];
      value.textContent = picked ? picked.text : '';
    }

    function place() {
      if (phone()) {
        dialog.style.left = '';
        dialog.style.top = '';
        dialog.style.width = '';
        return;
      }
      var box = trigger.getBoundingClientRect();
      var vw = document.documentElement.clientWidth;
      var vh = document.documentElement.clientHeight;
      var want = Math.max(box.width, MIN_WIDTH);
      dialog.style.width = Math.min(want, MAX_WIDTH, vw - GUTTER * 2) + 'px';
      var w = dialog.offsetWidth;
      var h = dialog.offsetHeight;
      var left = box.left;
      if (left + w > vw - GUTTER) left = box.right - w;
      if (left < GUTTER) left = GUTTER;
      var top = box.bottom + OFFSET;
      if (top + h > vh - GUTTER) {
        var above = box.top - OFFSET - h;
        top = above >= GUTTER ? above : Math.max(GUTTER, vh - GUTTER - h);
      }
      dialog.style.left = Math.round(left) + 'px';
      dialog.style.top = Math.round(top) + 'px';
    }

    function finish(restoreFocus) {
      dialog.classList.remove('is-open');
      if (dialog.open) dialog.close();
      document.body.classList.remove('pp-select-open');
      trigger.setAttribute('aria-expanded', 'false');
      if (openInstance === api) openInstance = null;
      /* A trigger the facets repaint has already replaced is not focusable and
         focusing it would drop focus on the body. */
      if (restoreFocus !== false && trigger.isConnected && trigger.getClientRects().length) trigger.focus();
    }

    function closePanel(restoreFocus) {
      if (!dialog.open) return;
      if (PP.reducedMotion()) {
        finish(restoreFocus);
        return;
      }
      dialog.classList.remove('is-open');
      document.body.classList.remove('pp-select-open');
      trigger.setAttribute('aria-expanded', 'false');
      var done = false;
      function end(event) {
        /* Only the sheet's own move ends the close. transitionend bubbles, and
           the ::backdrop reports the dialog itself as its target. */
        if (event && event.target !== sheet) return;
        if (done) return;
        done = true;
        dialog.removeEventListener('transitionend', end);
        finish(restoreFocus);
      }
      dialog.addEventListener('transitionend', end);
      setTimeout(function () { end(); }, CLOSE_FALLBACK);
    }

    function openPanel() {
      if (dialog.open) return;
      if (openInstance && openInstance !== api) openInstance.close(false);
      sync();
      dialog.showModal();
      document.body.classList.add('pp-select-open');
      trigger.setAttribute('aria-expanded', 'true');
      place();
      openInstance = api;
      requestAnimationFrame(function () { dialog.classList.add('is-open'); });
      var current = list.querySelector('[aria-selected="true"]') || list.firstElementChild;
      if (current) {
        current.focus();
        /* CENTRE IT, rather than leaving focus() to scroll the minimum it can,
           which lands the current value against whichever edge it came in from.
           `offsetTop` is relative to the list because the stylesheet positions
           it.

           THIS IS INERT IN THE SHOP'S CURRENT STATE AND THAT IS SAID PLAINLY
           rather than left for the next reader to discover. The arithmetic
           clamps: at 390x844 the country list is 28 rows of 48px in a 520px
           box, so the greatest reachable scrollTop is 1344 - 520 = 824, while
           centring the selected row would want 1296 - 236 = 1060. United States
           is the LAST of the twenty eight, so it sits against the bottom edge
           after this line exactly as it did before it, and the capture of that
           sheet is identical either side of this change. The line earns its
           place for every other value -- a shopper already switched to France
           or to Spain opens the list on their own country, mid panel -- and for
           the sort control the day its current value is not near the end. It is
           not a measured improvement on what ships today. */
        var centred = current.offsetTop - (list.clientHeight - current.offsetHeight) / 2;
        list.scrollTop = centred > 0 ? centred : 0;
      }
    }

    /* THE PANEL LEAVES BEFORE THE CHANGE IS ANNOUNCED, and instantly rather than
       sliding. Two reasons, both structural. On the country row the change makes
       header.js submit the localization form, so the page is navigating and a
       600ms exit would be animating a document that is on its way out. On the
       sort control facets.js is about to replace the toolbar by innerHTML, which
       takes this trigger's node with it: focus is handed back to the trigger
       first so that facets.js records THIS id as the thing to restore, and the
       re-arm below has rebuilt a trigger under the same id by the time it looks. */
    function commit(next) {
      var changed = select.value !== next;
      finish(true);
      if (!changed) return;
      select.value = next;
      sync();
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      if (dialog.open) closePanel();
      else openPanel();
    });

    /* A listbox opens on ArrowDown and ArrowUp as well as on the press a button
       already answers, which is what a keyboard user reaches for first. */
    trigger.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      openPanel();
    });

    if (label) {
      label.addEventListener('click', function () {
        if (!dialog.open) openPanel();
      });
    }

    list.addEventListener('click', function (event) {
      var row = event.target.closest ? event.target.closest('[data-pp-select-option]') : null;
      if (!row) return;
      commit(row.getAttribute('data-pp-select-value'));
    });

    /* Roving tabindex rather than aria-activedescendant: the option really holds
       focus, so the focus ring is the browser's own and Enter and Space are the
       button's own activation rather than two more key cases to write. */
    list.addEventListener('keydown', function (event) {
      var rows = options();
      var from = rows.indexOf(document.activeElement);
      var to = -1;
      if (event.key === 'ArrowDown') to = from + 1;
      else if (event.key === 'ArrowUp') to = from - 1;
      else if (event.key === 'Home') to = 0;
      else if (event.key === 'End') to = rows.length - 1;
      else return;
      event.preventDefault();
      if (to < 0) to = 0;
      if (to > rows.length - 1) to = rows.length - 1;
      for (var n = 0; n < rows.length; n += 1) rows[n].tabIndex = n === to ? 0 : -1;
      rows[to].focus();
    });

    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closePanel();
    });

    /* A click whose target is the dialog itself landed on the ::backdrop: on a
       phone that is everything above the sheet, on a desktop everything outside
       the list. */
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closePanel();
    });

    /* Crossing the phone breakpoint changes which of the two layouts the panel
       is in, and a panel placed as a desktop list is wrong the moment it becomes
       a sheet. Closing is honest; re-placing mid gesture is not. */
    function onResize() {
      /* Unbinds itself once its panel has left the document, which a facets
         repaint does on every filter change: without this the page would keep
         one dead listener per repaint. */
      if (!dialog.isConnected) {
        window.removeEventListener('resize', onResize);
        return;
      }
      if (dialog.open) closePanel(false);
    }
    window.addEventListener('resize', onResize);

    /* Anything else that writes the select -- a filter reconcile, a restored
       history entry -- is reflected rather than fought. */
    select.addEventListener('change', sync);

    var api = {
      close: function (restoreFocus) { closePanel(restoreFocus); }
    };

    sync();
  }

  function scan(scope) {
    var roots = (scope && scope.querySelectorAll ? scope : document).querySelectorAll('[data-pp-select]');
    for (var i = 0; i < roots.length; i += 1) arm(roots[i]);
  }

  scan();

  /* The editor re-renders a section's markup with no unload, and facets.js
     announces its own repaint for exactly this kind of listener. Both hand back
     a fresh, unarmed wrapper; arm() is idempotent on the ones that survived. */
  document.addEventListener('shopify:section:load', function (event) { scan(event.target); });
  document.addEventListener('pp:facets:repaint', function () { scan(document); });
})();
